import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onlinePaymentService } from '../../services/onlinePaymentService.js';
import Button from './Button.js';

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

/**
 * Shows UPI QR (Razorpay gateway or company UPI ID) and completes payment.
 * Gateway: polls until fulfilled.
 * Company UPI: agent taps "Payment received" after customer pays.
 */
export default function PaymentQrModal({
  intent,
  open,
  onSuccess,
  onCancel,
  title = 'Scan UPI QR to Pay',
}) {
  const [current, setCurrent] = useState(intent || null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const doneRef = useRef(false);
  const timerRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const isMobile = useMemo(() => isMobileDevice(), []);

  useEffect(() => {
    setCurrent(intent || null);
    doneRef.current = false;
    setError('');
  }, [intent]);

  const manualConfirm = Boolean(
    intent?.manual_confirm || intent?.provider === 'company_upi',
  );

  useEffect(() => {
    if (!open || !intent?.intent_id || manualConfirm) return undefined;

    const intentId = intent.intent_id;
    const tick = async () => {
      if (doneRef.current) return;
      try {
        setPolling(true);
        const res = await onlinePaymentService.getIntent(intentId);
        const next = res.data?.payment_intent || res.data?.data;
        if (next) {
          setCurrent((prev) => {
            const merged = { ...next };
            if (!merged.qr_image_content && prev?.qr_image_content) {
              merged.qr_image_content = prev.qr_image_content;
            }
            if (!merged.qr_image_url && prev?.qr_image_url) {
              merged.qr_image_url = prev.qr_image_url;
            }
            if (!merged.payment_url && prev?.payment_url) {
              merged.payment_url = prev.payment_url;
            }
            return merged;
          });
          if (next.status === 'fulfilled') {
            doneRef.current = true;
            if (timerRef.current) clearInterval(timerRef.current);
            onSuccessRef.current?.(next);
            return;
          }
          if (['expired', 'failed', 'cancelled'].includes(next.status)) {
            doneRef.current = true;
            if (timerRef.current) clearInterval(timerRef.current);
            setError(next.error_message || `Payment ${next.status}`);
          }
        }
        if (res.data?.warning) {
          setError(res.data.warning);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to check payment status');
      } finally {
        setPolling(false);
      }
    };

    tick();
    timerRef.current = setInterval(tick, 3000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, intent?.intent_id, manualConfirm]);

  if (!open || !current) return null;

  const isSafePaymentPageUrl = (value) => {
    if (!value || typeof value !== 'string') return false;
    const text = value.trim();
    if (!/^https?:\/\//i.test(text)) return false;
    const lower = text.toLowerCase();
    if (lower.startsWith('data:')) return false;
    if (/\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(lower)) return false;
    if (lower.includes('/image') || (lower.includes('api.razorpay.com') && lower.includes('qr_codes'))) {
      return false;
    }
    if (lower.includes('rzp.io/rzp/')) return false;
    return true;
  };

  const isUpiIntentUri = (value) => {
    if (!value || typeof value !== 'string') return false;
    return /^upi:\/\//i.test(value.trim());
  };

  const content = current.qr_image_content ? String(current.qr_image_content) : '';
  const url = current.qr_image_url ? String(current.qr_image_url) : '';
  const isNativeUpiQr = Boolean(current.is_native_upi_qr || manualConfirm);
  const urlLooksLikeImage = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url)
    || url.startsWith('data:image')
    || /rzp\.io\/i\//i.test(url);
  const qrSrc = content
    ? (content.startsWith('data:') ? content : `data:image/png;base64,${content}`)
    : (urlLooksLikeImage ? url : null);

  const upiPayUri = current.upi_pay_uri || (
    isUpiIntentUri(current.payment_url) ? current.payment_url : null
  );
  const paymentUrl = isSafePaymentPageUrl(current.payment_url)
    ? current.payment_url
    : (isSafePaymentPageUrl(url) ? url : null);

  const handleCancel = async () => {
    try {
      setCancelling(true);
      if (current.status === 'pending') {
        await onlinePaymentService.cancelIntent(current.intent_id);
      }
    } catch (_) {
      // ignore cancel errors; still close UI
    } finally {
      setCancelling(false);
      doneRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      onCancel?.(current);
    }
  };

  const handleConfirmPaid = async () => {
    try {
      setConfirming(true);
      setError('');
      const res = await onlinePaymentService.confirmIntent(current.intent_id);
      const next = res.data?.payment_intent || res.data?.data;
      if (next) setCurrent(next);
      doneRef.current = true;
      onSuccessRef.current?.(next || current);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not confirm payment');
    } finally {
      setConfirming(false);
    }
  };

  const openUpiPayment = () => {
    if (upiPayUri && isUpiIntentUri(upiPayUri)) {
      window.location.href = upiPayUri;
      return;
    }
    if (!paymentUrl || !isSafePaymentPageUrl(paymentUrl)) return;
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  };

  const canOpenUpi = Boolean(upiPayUri || paymentUrl);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-1">{title}</h2>
        <p className="text-sm text-gray-500 mb-4">
          Amount:{' '}
          <span className="font-semibold text-gray-900">
            ₹{Number(current.amount || 0).toFixed(2)}
          </span>
          {current.payment_mode ? (
            <span className="ml-2 uppercase text-xs tracking-wide text-gray-400">
              {current.payment_mode}
            </span>
          ) : null}
        </p>

        <div className="flex flex-col items-center gap-3 mb-4">
          {qrSrc ? (
            <div className="w-full max-w-[20rem] aspect-square rounded-2xl border border-gray-200 bg-white p-3 sm:p-4 shadow-sm flex items-center justify-center">
              <img
                src={qrSrc}
                alt="UPI QR Code"
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-full max-w-[20rem] aspect-square flex items-center justify-center border border-dashed border-gray-300 rounded-2xl text-sm text-gray-500">
              QR unavailable
            </div>
          )}

          {canOpenUpi ? (
            <Button
              variant="primary"
              type="button"
              onClick={openUpiPayment}
              className="w-full"
              disabled={['fulfilled', 'paid', 'cancelled', 'expired'].includes(current.status)}
            >
              {isMobile ? 'Pay with UPI app (PhonePe / GPay)' : 'Open UPI payment'}
            </Button>
          ) : null}

          <p className="text-xs text-center text-gray-500">
            {manualConfirm
              ? (current.provider === 'branch_upi'
                ? 'Customer scans this branch UPI QR. After they pay, tap Payment received. This amount still has to be settled.'
                : 'Customer scans this company UPI QR. After they pay, tap Payment received to create the shipment.')
              : isNativeUpiQr
                ? 'Scan with PhonePe / GPay / any UPI app. Payment opens in the app directly.'
                : isMobile
                  ? 'On this phone: tap “Pay with UPI app” to open PhonePe / GPay directly.'
                  : 'Counter use: customer can scan this QR. If a browser opens, tap Pay / UPI there (Razorpay UPI QR product not active on account yet).'}
            {!manualConfirm && polling ? ' Checking…' : ''}
          </p>
          <p className="text-xs text-gray-400">
            Status: <span className="font-medium text-gray-700">{current.status}</span>
          </p>
          {error ? <p className="text-xs text-red-600 text-center">{error}</p> : null}
        </div>

        <div className="flex gap-3 justify-end flex-wrap">
          <Button
            variant="secondary"
            type="button"
            onClick={handleCancel}
            loading={cancelling}
            disabled={current.status === 'fulfilled' || current.status === 'paid' || confirming}
          >
            Cancel
          </Button>
          {manualConfirm ? (
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirmPaid}
              loading={confirming}
              disabled={['fulfilled', 'cancelled', 'expired'].includes(current.status) || cancelling}
            >
              Payment received
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
